import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  Box, Button, Typography, CircularProgress, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Select, MenuItem, Snackbar, Checkbox, TextField
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { ArrowBack, Refresh, PlayArrow, Send as SendIcon, OpenInNew as OpenInNewIcon, Link as LinkIcon } from '@material-ui/icons'
import axios from 'axios'
import VendorProductDetail from '../../VendorCatalog/VendorProductDetail'
import MatchSuggestionsPanel from '../LineItems/MatchSuggestionsPanel'

const STATUS_CHIPS = {
  confirmed: { bg: '#e8f5e9', color: '#2e7d32', label: 'Confirmed' },
  quantity_conflict: { bg: '#fff3e0', color: '#e65100', label: 'Qty Conflict' },
  needs_review: { bg: '#e3f2fd', color: '#1565c0', label: 'Needs Review' },
  included_by_other: { bg: '#f3e5f5', color: '#7b1fa2', label: 'Included' },
  ignored: { bg: '#f5f5f5', color: '#9e9e9e', label: 'Ignored' },
  not_in_scope: { bg: '#ffebee', color: '#b71c1c', label: 'Not In Scope' }
}

const CATEGORIES = [
  'Irrigation', 'Chemical Applications', 'Demo', 'Earthwork', 'Hardscape', 'Planting',
  'Lighting', 'Water Feature', 'Signage', 'Turf', 'General', 'Other'
]

const CATEGORY_COLORS = {
  'Irrigation': '#1565c0',
  'Chemical Applications': '#00897b',
  'Demo': '#c62828',
  'Earthwork': '#6d4c41',
  'Hardscape': '#546e7a',
  'Planting': '#2e7d32',
  'Lighting': '#f9a825',
  'Water Feature': '#0277bd',
  'Signage': '#7b1fa2',
  'Turf': '#558b2f',
  'General': '#616161',
  'Other': '#9e9e9e'
}

const KIND_LABELS = {
  material: 'Material',
  service: 'Service',
  system: 'System',
  allowance: 'Allowance',
  bid_output_only: 'Bid Output',
  reference_only: 'Reference'
}

const CATEGORY_SERVICE_PREFIX = {
  'Irrigation': 'irr_',
  'Planting': 'planting_',
  'Hardscape': 'hardscape_',
  'Lighting': 'lighting_',
  'Demo': 'demo_',
  'Earthwork': 'earthwork_',
  'Turf': 'turf_',
  'Water Feature': 'water_feature_',
  'Signage': 'signage_'
}

const SERVICE_ROLE_META = {
  assembly: { label: 'Assembly', group: 'Assemblies / Packages', color: '#6a1b9a', bg: '#f3e5f5', rank: 0 },
  base_service: { label: 'Service', group: 'Base Services', color: '#1565c0', bg: '#e3f2fd', rank: 1 },
  child_service: { label: 'Child', group: 'Child Services', color: '#ef6c00', bg: '#fff3e0', rank: 2 }
}

const serviceRoleMeta = (service) => SERVICE_ROLE_META[service?.serviceRole] || SERVICE_ROLE_META.base_service

const serviceOptionLabel = (service) => {
  if (!service) return ''
  return service.name || service.serviceId || ''
}

const serviceSearchText = (service) => {
  if (!service) return ''
  return [
    service.name,
    service.serviceName,
    service.serviceId,
    service.category,
    service.serviceCategory,
    ...(service.aliases || [])
  ].filter(Boolean).join(' ').toLowerCase()
}

const serviceMatchesSearch = (service, query) => {
  const q = String(query || '').trim().toLowerCase()
  return !q || serviceSearchText(service).includes(q)
}

const serviceMatchesCategory = (service, category) => {
  if (!service || !category) return false
  const serviceCategory = String(service.category || '').toLowerCase()
  const activeCategory = String(category || '').toLowerCase()
  if (serviceCategory === activeCategory) return true
  if (serviceCategory.includes(activeCategory) || activeCategory.includes(serviceCategory)) return true
  const prefix = CATEGORY_SERVICE_PREFIX[category]
  return !!prefix && (service.serviceId || '').startsWith(prefix)
}

const sortServicesForCategory = (services, category) => {
  return [...(services || [])].sort((a, b) => {
    const aMatch = serviceMatchesCategory(a, category) ? 1 : 0
    const bMatch = serviceMatchesCategory(b, category) ? 1 : 0
    if (aMatch !== bMatch) return bMatch - aMatch
    const roleDiff = serviceRoleMeta(a).rank - serviceRoleMeta(b).rank
    if (roleDiff !== 0) return roleDiff
    const categoryDiff = String(a.category || '').localeCompare(String(b.category || ''))
    if (categoryDiff !== 0) return categoryDiff
    return serviceOptionLabel(a).localeCompare(serviceOptionLabel(b))
  })
}

const ServiceAutocomplete = ({ services, value, category, onChange, placeholder = 'Link service', width = 220 }) => {
  const options = useMemo(() => sortServicesForCategory(services, category), [services, category])
  const selected = useMemo(() => {
    return options.find(service => service.serviceId === value) ||
      (services || []).find(service => service.serviceId === value) ||
      null
  }, [options, services, value])

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(event, service) => onChange(service ? service.serviceId : '')}
      getOptionLabel={serviceOptionLabel}
      getOptionSelected={(option, selectedOption) => option.serviceId === selectedOption.serviceId}
      filterOptions={(serviceOptions, state) => serviceOptions.filter(service => serviceMatchesSearch(service, state.inputValue))}
      groupBy={(service) => serviceRoleMeta(service).group}
      size="small"
      style={{ width }}
      renderOption={(service) => {
        const role = serviceRoleMeta(service)
        const isCategoryMatch = serviceMatchesCategory(service, category)
        return (
          <Box display="flex" alignItems="center" style={{ width: '100%', gap: 6 }}>
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" style={{ fontSize: 12, lineHeight: 1.25 }}>{serviceOptionLabel(service)}</Typography>
              <Typography variant="caption" style={{ color: '#777', fontSize: 10 }}>
                {service.category || 'Other'} · {service.serviceId}
              </Typography>
            </Box>
            {isCategoryMatch && (
              <Chip size="small" label="Active" style={{ height: 18, fontSize: 9, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
            )}
            <Chip size="small" label={role.label} style={{ height: 18, fontSize: 9, backgroundColor: role.bg, color: role.color }} />
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            style: { ...params.InputProps.style, fontSize: 12, backgroundColor: '#fff' }
          }}
        />
      )}
    />
  )
}

const fmt = (v) => {
  if (!v || v === 0) return '—'
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const hasPositiveNumber = (v) => Number(v) > 0

const itemSources = (item) => Array.isArray(item.sources) ? item.sources : []

const sourceArray = (item, field) => Array.isArray(item[field]) ? item[field] : []

const plainObject = (value) => {
  if (!value) return {}
  if (value instanceof Map) return Object.fromEntries(value.entries())
  return typeof value === 'object' ? value : {}
}

const addInputKey = (set, key) => {
  const normalized = String(key || '').trim()
  if (normalized) set.add(normalized)
}

const referencedInputKeys = (service) => {
  const keys = new Set()
  const components = service?.components || []
  const defaultMaterials = service?.defaultMaterials || []
  const childRecipes = service?.childRecipes || []
  components.forEach(component => {
    if (component.quantityMode === 'per_input') addInputKey(keys, component.inputSlotKey)
    const formulaInputKeys = component.formulaInputKeys || []
    formulaInputKeys.forEach(key => addInputKey(keys, key))
  })
  defaultMaterials.forEach(material => {
    addInputKey(keys, material.inputSlotKey)
    const formulaInputKeys = material.formulaInputKeys || []
    formulaInputKeys.forEach(key => addInputKey(keys, key))
  })
  childRecipes.forEach(child => {
    const inputMapping = child.inputMapping || []
    inputMapping.forEach(mapping => addInputKey(keys, mapping.parentInputKey))
  })
  return keys
}

const requiredInputSlots = (service) => {
  const referenced = referencedInputKeys(service)
  return (service?.inputSlots || []).filter(slot => {
    if (!slot?.key) return false
    if (slot.key === 'assembly_qty' || slot.key === 'assembly_unit') return false
    if ((slot.tags || []).includes('optional')) return false
    return referenced.has(slot.key) || (slot.tags || []).includes('required') || slot.isSizeClassInput
  })
}

const parseVariableValue = (value) => {
  const raw = String(value ?? '').trim()
  const num = Number(raw)
  return raw !== '' && Number.isFinite(num) ? num : raw
}

const hasInputValue = (value) => value !== undefined && value !== null && value !== ''

const quantityForInputInference = (item) => {
  if (item.resolvedQty != null) return item.resolvedQty
  if (item.userQtyOverride != null) return item.userQtyOverride
  return item.quantity
}

const inferSingleMissingInput = (item, service, values) => {
  const slotsNeedingValue = requiredInputSlots(service).filter(slot =>
    slot.defaultValue == null && !hasInputValue(values[slot.key])
  )
  if (slotsNeedingValue.length !== 1) return null

  const qty = Number(quantityForInputInference(item))
  if (!Number.isFinite(qty) || qty <= 0) return null

  values[slotsNeedingValue[0].key] = qty
  return { key: slotsNeedingValue[0].key, value: qty }
}

const itemInputValues = (item, service) => {
  const values = { ...plainObject(item.variables) }
  const qty = item.resolvedQty ?? item.userQtyOverride ?? item.quantity
  if (qty !== undefined && qty !== null && qty !== '') values.assembly_qty = qty
  if (item.resolvedUnit || item.unit) values.assembly_unit = item.resolvedUnit || item.unit
  const inputSlots = service?.inputSlots || []
  inputSlots.forEach(slot => {
    if (!slot?.key) return
    if ((values[slot.key] === undefined || values[slot.key] === null || values[slot.key] === '') && slot.defaultValue != null) {
      values[slot.key] = slot.defaultValue
    }
  })
  inferSingleMissingInput(item, service, values)
  return values
}

const missingInputSlots = (item, service) => {
  const values = itemInputValues(item, service)
  return requiredInputSlots(service).filter(slot =>
    (values[slot.key] === undefined || values[slot.key] === null || values[slot.key] === '') && slot.defaultValue == null
  )
}

const inputReviewSlots = (item, service) => {
  if (!service || !item.serviceId || item.projectServiceId) return []
  if (['ignored', 'not_in_scope', 'included_by_other'].includes(item.status)) return []
  const missing = missingInputSlots(item, service)
  if (missing.length > 0) return missing
  const inferred = inferredInputKeys(item, service)
  if (inferred.size > 0) return requiredInputSlots(service).filter(slot => inferred.has(slot.key))
  return []
}

const inferredInputKeys = (item, service) => {
  const variables = plainObject(item.variables)
  const values = { ...variables }
  const inferred = inferSingleMissingInput(item, service, values)
  return inferred ? new Set([inferred.key]) : new Set()
}

const evidenceCounts = (item) => ({
  bid: sourceArray(item, 'outputLines').length || itemSources(item).filter(s => s.sourceType === 'bid_response').length,
  takeoff: sourceArray(item, 'takeoffSources').length || itemSources(item).filter(s => s.sourceType === 'plan_extraction' || s.sourceType === 'manual').length,
  vendor: sourceArray(item, 'vendorSources').length || itemSources(item).filter(s => s.sourceType === 'vendor_quote').length,
  service: sourceArray(item, 'serviceLinks').length + (item.serviceId ? 1 : 0)
})

const normalizeCatalogSku = (value) => String(value || '').replace(/[\s-]/g, '').toUpperCase()

const vendorCatalogRefs = (item) => {
  const refs = []
  const seen = new Set()
  const add = (source = {}) => {
    const sku = String(source.vendorSku || source.sku || source.vendorItemNumber || source.itemNumber || '').trim()
    const productId = String(source.vendorCatalogProductId || source.catalogProductId || source.vendorCatalogItemId || '').trim()
    if (!sku && !productId) return
    const key = `${normalizeCatalogSku(sku)}:${productId}`
    if (seen.has(key)) return
    seen.add(key)
    refs.push({
      sku,
      productId,
      vendor: source.vendor || source.vendorName || source.sourceLabel || null
    })
  }

  add({ vendorSku: item.vendorSku, vendorCatalogProductId: item.vendorCatalogProductId, vendor: item.vendor })
  add(item.vendorPricing)
  sourceArray(item, 'vendorSources').forEach(add)
  itemSources(item).filter(s => s.sourceType === 'vendor_quote').forEach(add)

  return refs
}

const pickCatalogProduct = (refs, results) => {
  const products = Array.isArray(results) ? results : []
  if (products.length === 0) return null
  const refSkus = new Set(refs.map(ref => normalizeCatalogSku(ref.sku)).filter(Boolean))
  return products.find(product => refSkus.has(normalizeCatalogSku(product.vendorItemNumber))) || products[0]
}

const VendorCatalogLinkChips = ({ item, url, onOpenProduct, onMessage }) => {
  const refs = vendorCatalogRefs(item)
  const [loadingKey, setLoadingKey] = useState('')

  if (refs.length === 0) return null

  const openRef = async (ref) => {
    const lookupKey = ref.productId || ref.sku
    if (!lookupKey) return
    if (ref.productId) {
      onOpenProduct(ref.productId)
      return
    }
    try {
      setLoadingKey(lookupKey)
      const res = await axios.get(`${url}/api/vendor-catalogs/products/search`, {
        params: { q: ref.sku, limit: 8, mappingStatus: 'all' }
      })
      const product = pickCatalogProduct([ref], res.data?.results)
      if (product?._id) {
        onOpenProduct(product._id)
      } else {
        onMessage(`No local vendor catalog item found for SKU ${ref.sku}`)
      }
    } catch (err) {
      console.error('Vendor catalog lookup error:', err)
      onMessage(`Could not load vendor catalog item for SKU ${ref.sku}`)
    } finally {
      setLoadingKey('')
    }
  }

  return (
    <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
      {refs.slice(0, 4).map((ref) => {
        const key = ref.productId || ref.sku
        const label = ref.sku ? `SKU ${ref.sku}` : 'Vendor Catalog'
        return (
          <Chip
            key={key}
            size="small"
            clickable
            icon={loadingKey === key ? <CircularProgress size={10} /> : <LinkIcon style={{ fontSize: 12 }} />}
            label={label}
            title="Open vendor catalog details"
            onClick={(e) => {
              e.stopPropagation()
              openRef(ref)
            }}
            style={{
              height: 20,
              fontSize: 10,
              backgroundColor: '#f4f0df',
              border: '1px solid #7b7560',
              color: '#3d4034',
              fontFamily: 'monospace'
            }}
          />
        )
      })}
    </Box>
  )
}

const selectedSourceTotal = (sources) => {
  const selected = sources.filter(source => source.selected && Number(source.quantity) > 0)
  if (selected.length === 0) return null
  return {
    quantity: selected.reduce((sum, source) => sum + Number(source.quantity || 0), 0),
    unit: selected.find(source => source.unit)?.unit || '',
    count: selected.length
  }
}

const EvidenceSourcePicker = ({ item, onSave }) => {
  const vendorSources = sourceArray(item, 'vendorSources')
  const takeoffSources = sourceArray(item, 'takeoffSources')
  const bidSources = itemSources(item).filter(source => source.sourceType === 'bid_response' && Number(source.quantity) > 0)
  const hasSelectable = vendorSources.length > 0 || takeoffSources.length > 0 || bidSources.length > 0

  if (!hasSelectable) return null

  const saveSelected = (patch, selectedType, total, message) => {
    if (!total || !Number.isFinite(total.quantity)) {
      onSave({
        ...patch,
        selectedQtySource: '',
        qtySource: item.qtySource || '',
        status: item.status
      }, 'No evidence rows selected')
      return
    }

    onSave({
      ...patch,
      userQtyOverride: total.quantity,
      resolvedQty: total.quantity,
      resolvedUnit: total.unit || item.resolvedUnit || item.unit || '',
      unit: total.unit || item.unit || '',
      qtySource: selectedType,
      selectedQtySource: `${selectedType}:selected`,
      ...(item.status === 'quantity_conflict' ? { status: 'needs_review' } : {})
    }, message || `Resolved quantity set to ${fmt(total.quantity)} ${total.unit || item.unit || ''}`)
  }

  const toggleVendorSource = (idx, checked) => {
    const nextVendorSources = vendorSources.map((source, sourceIdx) => ({
      ...source,
      selected: sourceIdx === idx ? checked : !!source.selected
    }))
    const nextTakeoffSources = takeoffSources.map(source => ({ ...source, selected: false }))
    const pricingSource = nextVendorSources.find(source => source.selected && Number(source.unitPrice) > 0)
    const vendorPricing = pricingSource ? {
      ...(item.vendorPricing || {}),
      vendorQuoteId: pricingSource.vendorQuoteId || item.vendorPricing?.vendorQuoteId || null,
      vendorLineIndex: pricingSource.vendorLineIndex,
      vendorSku: pricingSource.vendorSku || item.vendorPricing?.vendorSku || null,
      description: pricingSource.description || item.vendorPricing?.description || '',
      quoteQuantity: pricingSource.quantity || 0,
      quoteUnit: pricingSource.unit || '',
      unitPrice: pricingSource.unitPrice || 0,
      extension: pricingSource.extension || 0,
      confirmed: true
    } : item.vendorPricing
    saveSelected(
      { vendorSources: nextVendorSources, takeoffSources: nextTakeoffSources, vendorPricing },
      'vendor_quote',
      selectedSourceTotal(nextVendorSources),
      'Vendor quote selection saved'
    )
  }

  const toggleTakeoffSource = (idx, checked) => {
    const nextTakeoffSources = takeoffSources.map((source, sourceIdx) => ({
      ...source,
      selected: sourceIdx === idx ? checked : !!source.selected
    }))
    const nextVendorSources = vendorSources.map(source => ({ ...source, selected: false }))
    saveSelected(
      { vendorSources: nextVendorSources, takeoffSources: nextTakeoffSources },
      'plan_extraction',
      selectedSourceTotal(nextTakeoffSources),
      'Plan takeoff selection saved'
    )
  }

  const useBidSource = (source, idx) => {
    const nextVendorSources = vendorSources.map(vendorSource => ({ ...vendorSource, selected: false }))
    const nextTakeoffSources = takeoffSources.map(takeoffSource => ({ ...takeoffSource, selected: false }))
    saveSelected(
      {
        vendorSources: nextVendorSources,
        takeoffSources: nextTakeoffSources,
        selectedQtySource: `bid_response:${idx}`
      },
      'bid_response',
      { quantity: Number(source.quantity || 0), unit: source.unit || item.unit || '', count: 1 },
      'Bid schedule quantity selected'
    )
  }

  const selectedVendor = selectedSourceTotal(vendorSources)
  const selectedTakeoff = selectedSourceTotal(takeoffSources)
  const activeTotal = selectedVendor || selectedTakeoff

  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr) 82px 48px',
    alignItems: 'center',
    gap: 6,
    padding: '4px 0',
    fontSize: 12,
    color: '#555'
  }

  return (
    <Box style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e0e0e0' }}>
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Quantity Source Picker</Typography>
        {activeTotal && (
          <Chip
            size="small"
            label={`${activeTotal.count} selected = ${fmt(activeTotal.quantity)} ${activeTotal.unit || item.unit || ''}`}
            style={{ height: 20, fontSize: 10, backgroundColor: '#e8f5e9', color: '#2e7d32' }}
          />
        )}
      </Box>

      {vendorSources.length > 0 && (
        <Box style={{ marginTop: 6 }}>
          <Typography variant="caption" style={{ color: '#e65100', fontWeight: 700 }}>Vendor quote rows</Typography>
          {vendorSources.map((source, idx) => (
            <Box key={`vendor-${source.vendorQuoteId || 'quote'}-${source.vendorLineIndex ?? idx}`} style={rowStyle}>
              <Checkbox
                size="small"
                checked={!!source.selected}
                onChange={(e) => toggleVendorSource(idx, e.target.checked)}
                style={{ padding: 0, color: '#e65100' }}
              />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {source.description || 'Vendor quote row'}
                {source.vendorSku ? <span style={{ color: '#888', marginLeft: 6 }}>SKU {source.vendorSku}</span> : null}
              </span>
              <span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{fmt(source.quantity)}</span>
              <span style={{ color: '#777' }}>{source.unit || item.unit || ''}</span>
            </Box>
          ))}
        </Box>
      )}

      {takeoffSources.length > 0 && (
        <Box style={{ marginTop: 8 }}>
          <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 700 }}>Plan / takeoff rows</Typography>
          {takeoffSources.map((source, idx) => (
            <Box key={`takeoff-${source.sourceId || source.sourceItemId || idx}`} style={rowStyle}>
              <Checkbox
                size="small"
                checked={!!source.selected}
                onChange={(e) => toggleTakeoffSource(idx, e.target.checked)}
                style={{ padding: 0, color: '#2e7d32' }}
              />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {source.documentId || source.sourceItemId || 'Plan takeoff'}
                {source.pageNumber != null ? <span style={{ color: '#888', marginLeft: 6 }}>Page {source.pageNumber}</span> : null}
              </span>
              <span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{fmt(source.quantity)}</span>
              <span style={{ color: '#777' }}>{source.unit || item.unit || ''}</span>
            </Box>
          ))}
        </Box>
      )}

      {bidSources.length > 0 && (
        <Box style={{ marginTop: 8 }}>
          <Typography variant="caption" style={{ color: '#1565c0', fontWeight: 700 }}>Bid schedule rows</Typography>
          {bidSources.map((source, idx) => (
            <Box key={`bid-${source.sourceId || idx}`} style={rowStyle}>
              <Button
                size="small"
                variant={item.selectedQtySource === `bid_response:${idx}` ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => useBidSource(source, idx)}
                style={{ minWidth: 36, width: 36, height: 22, padding: 0, fontSize: 9 }}
              >
                Use
              </Button>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {source.sourceLabel || 'Bid schedule row'}
              </span>
              <span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{fmt(source.quantity)}</span>
              <span style={{ color: '#777' }}>{source.unit || item.unit || ''}</span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

const SOURCE_STYLE = {
  bid: { bg: '#e3f2fd', color: '#1565c0', label: 'BID' },
  takeoff: { bg: '#e8f5e9', color: '#2e7d32', label: 'PLAN' },
  vendor: { bg: '#fff3e0', color: '#e65100', label: 'VQ' },
  service: { bg: '#f3e5f5', color: '#7b1fa2', label: 'SVC' }
}

const EvidencePill = ({ type, count }) => {
  const style = SOURCE_STYLE[type]
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '2px 6px',
      borderRadius: 10,
      backgroundColor: count > 0 ? style.bg : '#f5f5f5',
      color: count > 0 ? style.color : '#9e9e9e',
      fontSize: 10,
      fontWeight: 700
    }}>
      {style.label} {count}
    </span>
  )
}

const VariableEditor = ({ item, service, onSave }) => {
  const variables = plainObject(item.variables)
  const slots = useMemo(() => requiredInputSlots(service), [service])
  const slotKeySet = useMemo(() => new Set(slots.map(slot => slot.key)), [slots])
  const inferredKeys = useMemo(() => inferredInputKeys(item, service), [item, service])
  const initialRows = Object.entries(variables)
    .filter(([key]) => !slotKeySet.has(key))
    .map(([key, value]) => ({ key, value: String(value ?? '') }))
  const inputValues = itemInputValues(item, service)
  const initialSlotValues = slots.reduce((acc, slot) => {
    const current = inputValues[slot.key]
    acc[slot.key] = current != null ? String(current) : ''
    return acc
  }, {})
  const [rows, setRows] = useState(initialRows.length > 0 ? initialRows : [{ key: '', value: '' }])
  const [slotValues, setSlotValues] = useState(initialSlotValues)

  useEffect(() => {
    const nextVars = plainObject(item.variables)
    const nextRows = Object.entries(nextVars)
      .filter(([key]) => !slotKeySet.has(key))
      .map(([key, value]) => ({ key, value: String(value ?? '') }))
    setRows(nextRows.length > 0 ? nextRows : [{ key: '', value: '' }])
    const nextValues = itemInputValues(item, service)
    setSlotValues(slots.reduce((acc, slot) => {
      const current = nextValues[slot.key]
      acc[slot.key] = current != null ? String(current) : ''
      return acc
    }, {}))
  }, [item._id, item.variables, service, slotKeySet, slots])

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row))
  }

  const save = () => {
    const next = {}
    slots.forEach(slot => {
      const raw = String(slotValues[slot.key] ?? '').trim()
      if (raw !== '') next[slot.key] = parseVariableValue(raw)
    })
    rows.forEach(row => {
      const key = String(row.key || '').trim()
      if (!key) return
      next[key] = parseVariableValue(row.value)
    })
    onSave(next)
  }

  return (
    <Box>
      <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Recipe Inputs</Typography>
      {slots.length === 0 ? (
        <Typography variant="body2" style={{ color: '#999', marginTop: 6 }}>No template inputs required.</Typography>
      ) : (
        slots.map(slot => {
          const missing = (slotValues[slot.key] === undefined || slotValues[slot.key] === null || String(slotValues[slot.key]).trim() === '') && slot.defaultValue == null
          const inferred = inferredKeys.has(slot.key)
          return (
            <TextField
              key={slot.key}
              variant="outlined"
              size="small"
              fullWidth
              label={slot.label || slot.key}
              placeholder={slot.unit || slot.key}
              value={slotValues[slot.key] ?? ''}
              error={missing}
              helperText={missing ? 'Required before pricing' : inferred ? `Prefilled from resolved quantity${slot.unit ? ` · ${slot.unit}` : ''}` : (slot.unit || '')}
              onChange={(e) => setSlotValues(prev => ({ ...prev, [slot.key]: e.target.value }))}
              style={{ marginTop: 8 }}
              InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
              InputLabelProps={{ style: { fontSize: 12 } }}
            />
          )
        })
      )}
      <Typography variant="caption" style={{ display: 'block', fontWeight: 700, color: '#555', marginTop: 12 }}>Custom Variables</Typography>
      {rows.map((row, idx) => (
        <Box key={idx} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="key"
            value={row.key}
            onChange={(e) => updateRow(idx, 'key', e.target.value)}
            InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="value"
            value={row.value}
            onChange={(e) => updateRow(idx, 'value', e.target.value)}
            InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
          />
        </Box>
      ))}
      <Box style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <Button size="small" variant="outlined" onClick={() => setRows(prev => [...prev, { key: '', value: '' }])}>
          Add Variable
        </Button>
        <Button size="small" variant="contained" color="primary" onClick={save}>
          Save Inputs
        </Button>
      </Box>
    </Box>
  )
}

const ClassBreakdownEditor = ({ item, service, onSave }) => {
  const slots = (service?.inputSlots || []).filter(slot => slot?.isSizeClassInput && (slot.quantityClasses || []).length > 0)
  const [values, setValues] = useState(plainObject(item.classBreakdowns))

  useEffect(() => {
    setValues(plainObject(item.classBreakdowns))
  }, [item._id, item.classBreakdowns])

  if (slots.length === 0) return null

  const updateValue = (slotKey, classKey, value) => {
    setValues(prev => ({
      ...prev,
      [slotKey]: {
        ...(prev[slotKey] || {}),
        [classKey]: value
      }
    }))
  }

  const save = () => {
    const next = {}
    Object.entries(values || {}).forEach(([slotKey, breakdown]) => {
      const cleaned = {}
      Object.entries(breakdown || {}).forEach(([classKey, value]) => {
        const num = Number(value)
        if (Number.isFinite(num) && num > 0) cleaned[classKey] = num
      })
      if (Object.keys(cleaned).length > 0) next[slotKey] = cleaned
    })
    onSave(next)
  }

  return (
    <Box style={{ marginTop: 12 }}>
      <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Class Breakdowns</Typography>
      {slots.map(slot => (
        <Box key={slot.key} style={{ marginTop: 8 }}>
          <Typography variant="caption" style={{ color: '#777' }}>{slot.label || slot.key}</Typography>
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 6, marginTop: 4 }}>
            {(slot.quantityClasses || []).map(cls => (
              <TextField
                key={cls.classKey}
                variant="outlined"
                size="small"
                label={cls.label || cls.classKey}
                value={(values[slot.key] || {})[cls.classKey] ?? ''}
                onChange={(e) => updateValue(slot.key, cls.classKey, e.target.value)}
                InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
                InputLabelProps={{ style: { fontSize: 12 } }}
              />
            ))}
          </Box>
        </Box>
      ))}
      <Button size="small" variant="contained" color="primary" onClick={save} style={{ marginTop: 8 }}>
        Save Breakdowns
      </Button>
    </Box>
  )
}

const OutputAllocationEditor = ({ item, onSave }) => {
  const outputLines = sourceArray(item, 'outputLines')
  const [rows, setRows] = useState(outputLines)

  useEffect(() => {
    setRows(sourceArray(item, 'outputLines'))
  }, [item._id, item.outputLines])

  if (rows.length === 0) {
    return (
      <Box style={{ marginTop: 12 }}>
        <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Bid Output Allocations</Typography>
        <Typography variant="body2" style={{ color: '#999', marginTop: 6, fontSize: 12 }}>No bid output row linked.</Typography>
      </Box>
    )
  }

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((row, rowIdx) => rowIdx === idx ? { ...row, ...patch } : row))
  }

  const save = () => {
    onSave(rows.map(row => ({
      bidItemId: row.bidItemId,
      bidPackageId: row.bidPackageId,
      allocationBasis: row.allocationBasis || 'direct',
      allocationPercent: row.allocationPercent === '' || row.allocationPercent == null ? null : Number(row.allocationPercent),
      costAllocated: row.costAllocated === '' || row.costAllocated == null ? 0 : Number(row.costAllocated),
      sellAllocated: row.sellAllocated === '' || row.sellAllocated == null ? 0 : Number(row.sellAllocated),
      locked: !!row.locked
    })))
  }

  const percentTotal = rows.reduce((sum, row) => sum + (Number(row.allocationPercent) || 0), 0)
  const hasManualPercent = rows.some(row => row.allocationBasis === 'manual_percent' || row.allocationBasis === 'manual')

  return (
    <Box style={{ marginTop: 12 }}>
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Bid Output Allocations</Typography>
        {hasManualPercent && (
          <Chip
            size="small"
            label={`${percentTotal}% allocated`}
            style={{ height: 20, fontSize: 10, backgroundColor: Math.abs(percentTotal - 100) < 0.01 ? '#e8f5e9' : '#fff3e0', color: Math.abs(percentTotal - 100) < 0.01 ? '#2e7d32' : '#e65100' }}
          />
        )}
      </Box>
      {rows.map((line, idx) => (
        <Box key={`${line.bidItemId || idx}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 140px 90px 90px 90px 64px', gap: 6, alignItems: 'center', marginTop: 6 }}>
          <Typography variant="body2" style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {line.bidItemId ? `Bid row ${String(line.bidItemId).slice(-6)}` : 'Bid row'}
          </Typography>
          <Select
            value={line.allocationBasis || 'direct'}
            onChange={(e) => updateRow(idx, { allocationBasis: e.target.value })}
            variant="outlined"
            size="small"
            style={{ fontSize: 12, backgroundColor: '#fff' }}
          >
            <MenuItem value="direct">Direct</MenuItem>
            <MenuItem value="proportional">Proportional</MenuItem>
            <MenuItem value="equal">Equal</MenuItem>
            <MenuItem value="manual_percent">Manual %</MenuItem>
            <MenuItem value="manual_amount">Manual Amount</MenuItem>
          </Select>
          <TextField
            variant="outlined"
            size="small"
            placeholder="%"
            value={line.allocationPercent ?? ''}
            onChange={(e) => updateRow(idx, { allocationPercent: e.target.value })}
            InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Cost $"
            value={line.costAllocated ?? ''}
            disabled={line.allocationBasis !== 'manual_amount' && line.allocationBasis !== 'manual_cost'}
            onChange={(e) => updateRow(idx, { costAllocated: e.target.value })}
            InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Sell $"
            value={line.sellAllocated ?? ''}
            disabled={line.allocationBasis !== 'manual_amount' && line.allocationBasis !== 'manual_sell'}
            onChange={(e) => updateRow(idx, { sellAllocated: e.target.value })}
            InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
          />
          <Box display="flex" alignItems="center">
            <Checkbox
              size="small"
              checked={!!line.locked}
              onChange={(e) => updateRow(idx, { locked: e.target.checked })}
              style={{ padding: 2 }}
            />
            <Typography variant="caption" style={{ color: '#777' }}>Lock</Typography>
          </Box>
        </Box>
      ))}
      <Button size="small" variant="contained" color="primary" onClick={save} style={{ marginTop: 8 }}>
        Save Allocation
      </Button>
    </Box>
  )
}

const BidEvidencePanel = ({ item, projectId, url }) => {
  const [loading, setLoading] = useState(false)
  const [bidItems, setBidItems] = useState([])
  const [error, setError] = useState('')

  const refs = useMemo(() => {
    const byPackage = new Map()
    const addRef = (packageId, itemId) => {
      if (!packageId || !itemId) return
      const pkg = String(packageId)
      if (!byPackage.has(pkg)) byPackage.set(pkg, new Set())
      byPackage.get(pkg).add(String(itemId))
    }
    itemSources(item)
      .filter(src => src.sourceType === 'bid_response')
      .forEach(src => addRef(src.sourcePackageId, src.sourceId))
    sourceArray(item, 'outputLines')
      .forEach(line => addRef(line.bidPackageId, line.bidItemId))
    return Array.from(byPackage.entries()).map(([packageId, ids]) => ({ packageId, ids }))
  }, [item])

  useEffect(() => {
    let cancelled = false
    const fetchBidEvidence = async () => {
      if (refs.length === 0) {
        setBidItems([])
        return
      }
      setLoading(true)
      setError('')
      try {
        const responses = await Promise.all(refs.map(ref =>
          axios.get(`${url}/api/bid-estimation/bid-packages/${ref.packageId}/items`)
            .then(res => ({ ref, items: Array.isArray(res.data) ? res.data : [] }))
        ))
        if (cancelled) return
        const rows = []
        responses.forEach(({ ref, items }) => {
          const wanted = new Set(Array.from(ref.ids))
          items
            .filter(bidItem => wanted.has(String(bidItem._id)))
            .forEach(bidItem => rows.push({ ...bidItem, sourcePackageId: ref.packageId }))
        })
        setBidItems(rows)
      } catch (err) {
        if (!cancelled) {
          console.error('Bid evidence fetch error:', err)
          setError('Could not load bid response evidence')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchBidEvidence()
    return () => { cancelled = true }
  }, [refs, url])

  if (refs.length === 0) return null

  return (
    <Box style={{ marginTop: 12 }}>
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Bid Response Match</Typography>
        {refs.length === 1 && (
          <Button
            size="small"
            variant="text"
            onClick={() => window.open(`/bid-estimation/${projectId}/bid-response/${refs[0].packageId}`, '_blank')}
            style={{ minWidth: 0, padding: '1px 4px', fontSize: 10, textTransform: 'none' }}
          >
            Open Bid Response
          </Button>
        )}
      </Box>
      {loading ? (
        <Typography variant="body2" style={{ color: '#777', marginTop: 6, fontSize: 12 }}>Loading bid response...</Typography>
      ) : error ? (
        <Typography variant="body2" style={{ color: '#b71c1c', marginTop: 6, fontSize: 12 }}>{error}</Typography>
      ) : bidItems.length === 0 ? (
        <Typography variant="body2" style={{ color: '#999', marginTop: 6, fontSize: 12 }}>No matching bid response rows found.</Typography>
      ) : (
        <Box style={{ marginTop: 6, border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden', backgroundColor: '#fff' }}>
          {bidItems.map(bidItem => (
            <Box key={bidItem._id} style={{ padding: '8px 10px', borderBottom: '1px solid #eeeeee' }}>
              <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <Typography variant="body2" style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>
                  {bidItem.itemNo ? `#${bidItem.itemNo} ` : ''}{bidItem.descriptionNormalized || bidItem.descriptionRaw || 'Bid item'}
                </Typography>
                <Typography variant="body2" style={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {fmt(bidItem.quantity)} {bidItem.unitNormalized || bidItem.unitRaw || ''}
                </Typography>
              </Box>
              <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4, color: '#777', fontSize: 11 }}>
                {bidItem.bidGroup?.name && <span>{bidItem.bidGroup.name}</span>}
                {bidItem.sourceRef?.sheetName && <span>{bidItem.sourceRef.sheetName}</span>}
                {bidItem.sourceRef?.pageNumber && <span>Page {bidItem.sourceRef.pageNumber}</span>}
                {bidItem.sourceRef?.dataRowIndex != null && <span>Row {bidItem.sourceRef.dataRowIndex + 1}</span>}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

const needsQuantity = (item) => {
  return item.status === 'quantity_conflict' ||
    (!hasPositiveNumber(item.userQtyOverride) && !hasPositiveNumber(item.quantity))
}

const hasPrice = (item) => {
  return !!item.projectServiceId ||
    hasPositiveNumber(item.vendorPricing?.unitPrice) ||
    hasPositiveNumber(item.vendorPricing?.totalPrice) ||
    hasPositiveNumber(item.unitPrice) ||
    hasPositiveNumber(item.totalPrice)
}

const ScopeReconciler = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const { url } = props

  const [project, setProject] = useState(null)
  const [items, setItems] = useState([])
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [sendingToEst, setSendingToEst] = useState(false)
  const [snackMsg, setSnackMsg] = useState('')
  const [selected, setSelected] = useState(new Set())
  const [bulkServiceId, setBulkServiceId] = useState('')
  const [bulkCategory, setBulkCategory] = useState('')
  const [dirtyIds, setDirtyIds] = useState(new Set())
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [readinessFilter, setReadinessFilter] = useState('all')
  const [autoLinking, setAutoLinking] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [vendorProductId, setVendorProductId] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [projRes, scopeRes, svcRes] = await Promise.all([
        axios.get(`${url}/api/bid-estimation/projects/${projectId}`),
        axios.get(`${url}/api/bid-estimation/projects/${projectId}/scope`),
        axios.get(`${url}/api/bid-estimation/service-templates`)
      ])
      setProject(projRes.data)
      setItems(Array.isArray(scopeRes.data) ? scopeRes.data : [])
      const templates = Array.isArray(svcRes.data) ? svcRes.data : []
      setServices(templates.map(t => ({ ...t, name: t.serviceName, category: t.serviceCategory })))
    } catch (err) {
      console.error('ScopeReconciler fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [url, projectId])

  useEffect(() => { fetchData() }, [fetchData])

  const handleGenerate = async () => {
    try {
      setGenerating(true)
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/scope/generate`)
      const d = res.data
      const g = d.generated || {}
      const parts = [`Generated ${g.total || 0} assembly items`]
      if (g.fromMatches) parts.push(`${g.fromMatches} from matched groups`)
      if (g.singleSource) parts.push(`${g.singleSource} single-source`)
      if (g.rollups) parts.push(`${g.rollups} rollups detected`)
      if (d.cleared) parts.push(`cleared ${d.cleared} old`)
      if (d.reconciliation?.conflicts) parts.push(`${d.reconciliation.conflicts} qty conflicts`)
      setSnackMsg(parts.join(' · '))
      fetchData()
    } catch (err) {
      console.error('Generate scope error:', err)
      setSnackMsg('Error generating assembly items')
    } finally {
      setGenerating(false)
    }
  }

  const handleServiceLink = async (itemId, serviceId) => {
    try {
      await axios.put(`${url}/api/bid-estimation/scope-items/${itemId}`, { serviceId: serviceId || null })
      setItems(prev => prev.map(item =>
        item._id === itemId ? { ...item, serviceId } : item
      ))
    } catch (err) {
      console.error('Service link error:', err)
    }
  }

  const handleItemPatch = async (itemId, patch, message = 'Assembly item updated') => {
    try {
      const res = await axios.put(`${url}/api/bid-estimation/scope-items/${itemId}`, patch)
      const saved = res.data || {}
      setItems(prev => prev.map(item =>
        item._id === itemId ? { ...item, ...saved } : item
      ))
      setSnackMsg(message)
    } catch (err) {
      console.error('Assembly item update error:', err)
      setSnackMsg('Failed to update assembly item')
    }
  }

  const handleSendToEstimating = async () => {
    const blockedByInputs = items.filter(item => {
      const service = services.find(s => s.serviceId === item.serviceId)
      return item.status === 'confirmed' && item.serviceId && !item.projectServiceId && missingInputSlots(item, service).length > 0
    })
    if (blockedByInputs.length > 0) {
      setSnackMsg(`${blockedByInputs.length} confirmed item${blockedByInputs.length === 1 ? '' : 's'} need recipe inputs before pricing`)
      return
    }
    try {
      setSendingToEst(true)
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/scope/send-to-estimating`)
      const warnings = res.data?.warnings || []
      if (warnings.length > 0) {
        setSnackMsg(`Created ${res.data.created} new services, updated ${res.data.updated} existing · ${warnings.length} need templates/components before pricing`)
        fetchData()
        return
      }
      setSnackMsg(`Created ${res.data.created} new services, updated ${res.data.updated} existing`)
      history.push(`/bid-estimation/${projectId}/estimating`)
    } catch (err) {
      setSnackMsg('Failed to send to estimating')
    } finally {
      setSendingToEst(false)
    }
  }

  const handleAutoLink = async () => {
    try {
      setAutoLinking(true)
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/scope/auto-link`)
      const { linked, unlinked } = res.data
      setSnackMsg(`Auto-linked ${linked} of ${linked + unlinked} items. ${unlinked} need manual linking.`)
      fetchData()
    } catch (err) {
      console.error('Auto-link error:', err)
      setSnackMsg('Error running auto-link')
    } finally {
      setAutoLinking(false)
    }
  }

  const handleBulkConfirm = async () => {
    const reviewItems = items.filter(i => i.status === 'needs_review' && i.serviceId)
    await Promise.all(reviewItems.map(item =>
      axios.put(`${url}/api/bid-estimation/scope-items/${item._id}`, { status: 'confirmed' })
    ))
    const confirmedIds = new Set(reviewItems.map(item => item._id))
    setItems(prev => prev.map(item =>
      confirmedIds.has(item._id) ? { ...item, status: 'confirmed' } : item
    ))
    setSnackMsg(`Confirmed ${reviewItems.length} items`)
  }

  // Save persists ALL pending changes (category, service, status) to the server
  const handleBulkSave = async () => {
    if (selected.size === 0) return
    const ids = [...selected]
    const updates = {}
    if (bulkCategory) updates.category = bulkCategory
    if (bulkServiceId) updates.serviceId = bulkServiceId

    // Collect per-item updates: shared bulk fields + any individual status toggles
    const savePromises = ids.map(id => {
      const item = items.find(i => i._id === id)
      const itemUpdates = { ...updates }
      // Include status if it was toggled (item is dirty)
      if (item && dirtyIds.has(id)) itemUpdates.status = item.status
      if (Object.keys(itemUpdates).length === 0) return null
      return axios.put(`${url}/api/bid-estimation/scope-items/${id}`, itemUpdates)
    }).filter(Boolean)

    if (savePromises.length === 0) return
    await Promise.all(savePromises)

    const selectedIds = new Set(ids)
    setItems(prev => prev.map(item =>
      selectedIds.has(item._id) ? { ...item, ...updates } : item
    ))
    setDirtyIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.delete(id))
      return next
    })
    const parts = []
    if (updates.category) parts.push(`category → ${updates.category}`)
    if (updates.serviceId) parts.push('service linked')
    const statusChanges = ids.filter(id => dirtyIds.has(id)).length
    if (statusChanges > 0) parts.push(`${statusChanges} status changes`)
    setSnackMsg(`Saved ${ids.length} items${parts.length ? ': ' + parts.join(', ') : ''}`)
    setSelected(new Set())
    setBulkCategory('')
    setBulkServiceId('')
  }

  // Confirm toggle: only un-confirm if ALL selected are confirmed; otherwise confirm all
  const handleBulkConfirmSelected = () => {
    const ids = [...selected]
    const selectedIds = new Set(ids)
    const selItems = items.filter(i => selectedIds.has(i._id))
    const allConfirmed = selItems.length > 0 && selItems.every(i => i.status === 'confirmed')
    const newStatus = allConfirmed ? 'needs_review' : 'confirmed'
    setItems(prev => prev.map(item =>
      selectedIds.has(item._id) ? { ...item, status: newStatus } : item
    ))
    setDirtyIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.add(id))
      return next
    })
  }

  const handleStatusChange = (itemId, newStatus) => {
    setItems(prev => prev.map(item =>
      item._id === itemId ? { ...item, status: newStatus } : item
    ))
    setDirtyIds(prev => new Set(prev).add(itemId))
  }

  // Not In Scope toggle: only un-mark if ALL selected are not_in_scope; otherwise mark all
  const handleBulkNotInScope = () => {
    const ids = [...selected]
    const selectedIds = new Set(ids)
    const selItems = items.filter(i => selectedIds.has(i._id))
    const allNotInScope = selItems.length > 0 && selItems.every(i => i.status === 'not_in_scope')
    const newStatus = allNotInScope ? 'needs_review' : 'not_in_scope'
    setItems(prev => prev.map(item =>
      selectedIds.has(item._id) ? { ...item, status: newStatus } : item
    ))
    setDirtyIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.add(id))
      return next
    })
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <CircularProgress />
      </Box>
    )
  }

  const conflictCount = items.filter(i => i.status === 'quantity_conflict').length
  const reviewCount = items.filter(i => i.status === 'needs_review').length
  const confirmedCount = items.filter(i => i.status === 'confirmed').length
  const serviceById = new Map(services.map(service => [service.serviceId, service]))
  const recipeInputMissingCount = items.filter(i => i.status === 'confirmed' && i.serviceId && !i.projectServiceId && missingInputSlots(i, serviceById.get(i.serviceId)).length > 0).length
  const recipeInputReviewCount = items.filter(i => inputReviewSlots(i, serviceById.get(i.serviceId)).length > 0).length
  const readyToSendCount = items.filter(i => i.status === 'confirmed' && i.serviceId && !i.projectServiceId && missingInputSlots(i, serviceById.get(i.serviceId)).length === 0).length
  const linkedReviewCount = items.filter(i => i.status === 'needs_review' && i.serviceId).length
  const sourceReviewCount = items.filter(i => ['needs_review', 'quantity_conflict'].includes(i.status)).length
  const needsQuantityCount = items.filter(needsQuantity).length
  const serviceLinkedCount = items.filter(i => !!i.serviceId).length
  const pricedCount = items.filter(hasPrice).length
  const outputReadyCount = items.filter(i => i.status === 'confirmed' && i.serviceId && i.projectServiceId).length

  const categoryFilteredItems = categoryFilter === 'all'
    ? items
    : categoryFilter === 'uncategorized'
      ? items.filter(i => !i.category)
      : items.filter(i => i.category === categoryFilter)
  const readinessFilteredItems = readinessFilter === 'input_review'
    ? categoryFilteredItems.filter(i => inputReviewSlots(i, serviceById.get(i.serviceId)).length > 0)
    : readinessFilter === 'source_review'
      ? categoryFilteredItems.filter(i => ['needs_review', 'quantity_conflict'].includes(i.status))
      : readinessFilter === 'needs_quantity'
        ? categoryFilteredItems.filter(needsQuantity)
        : categoryFilteredItems
  const filteredItems = statusFilter === 'all'
    ? readinessFilteredItems
    : readinessFilteredItems.filter(i => i.status === statusFilter)
  const selectedItems = items.filter(item => selected.has(item._id))
  const selectedCategories = [...new Set(selectedItems.map(item => item.category).filter(Boolean))]
  const activeServiceCategory = CATEGORIES.includes(categoryFilter)
    ? categoryFilter
    : selectedCategories.length === 1
      ? selectedCategories[0]
      : ''

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      {/* Header */}
      <div className="page-head">
        <div className="crumbs">
          <a onClick={() => history.push('/bid-estimation')}>Bid Estimation</a>
          <span className="sep">&rsaquo;</span>
          <a onClick={() => history.push(`/bid-estimation/${projectId}`)}>{project?.name || 'Project'}</a>
          <span className="sep">&rsaquo;</span>
          <span className="here">Assembly Workbench</span>
        </div>
        <div className="title-row">
          <h1>Assembly Workbench ({items.length})</h1>
          <div className="actions">
            <Button
              variant="outlined"
              size="small"
              startIcon={autoLinking ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
              onClick={handleAutoLink}
              disabled={autoLinking || items.length === 0}
              style={{ textTransform: 'none' }}
            >
              Auto-Link Services
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkConfirm}
              disabled={linkedReviewCount === 0}
            >
              Confirm All Linked ({linkedReviewCount})
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={sendingToEst ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
              onClick={handleSendToEstimating}
              disabled={sendingToEst || readyToSendCount === 0}
              style={{ backgroundColor: '#2e7d32', color: '#fff' }}
            >
              Send to Pricing ({readyToSendCount})
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
              onClick={handleGenerate}
              disabled={generating}
            >
              {items.length === 0 ? 'Generate Assembly Items' : 'Rebuild Items'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchData}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="hmi-body">
      {/* Readiness chips */}
      <Box style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={`${sourceReviewCount} Source Review`}
          onClick={() => {
            setStatusFilter('all')
            setReadinessFilter(readinessFilter === 'source_review' ? 'all' : 'source_review')
          }}
          style={{
            backgroundColor: readinessFilter === 'source_review' ? '#e65100' : '#fff3e0',
            color: readinessFilter === 'source_review' ? '#fff' : '#e65100',
            cursor: 'pointer'
          }}
        />
        <Chip
          size="small"
          label={`${needsQuantityCount} Needs Quantity`}
          onClick={() => {
            setStatusFilter('all')
            setReadinessFilter(readinessFilter === 'needs_quantity' ? 'all' : 'needs_quantity')
          }}
          style={{
            backgroundColor: readinessFilter === 'needs_quantity' ? '#b71c1c' : needsQuantityCount > 0 ? '#ffebee' : '#f5f5f5',
            color: readinessFilter === 'needs_quantity' ? '#fff' : needsQuantityCount > 0 ? '#b71c1c' : '#666',
            cursor: 'pointer'
          }}
        />
        <Chip
          size="small"
          label={`${serviceLinkedCount} Service Linked`}
          style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
        />
        <Chip
          size="small"
          label={`${recipeInputReviewCount} Input Review`}
          onClick={() => {
            setStatusFilter('all')
            setReadinessFilter(readinessFilter === 'input_review' ? 'all' : 'input_review')
          }}
          style={{
            backgroundColor: readinessFilter === 'input_review' ? '#b71c1c' : recipeInputMissingCount > 0 ? '#ffebee' : '#f5f5f5',
            color: readinessFilter === 'input_review' ? '#fff' : recipeInputMissingCount > 0 ? '#b71c1c' : '#666',
            cursor: 'pointer'
          }}
        />
        <Chip
          size="small"
          label={`${pricedCount} Priced`}
          style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
        />
        <Chip
          size="small"
          label={`${outputReadyCount} Output Ready`}
          style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
        />
      </Box>

      {/* Summary chips */}
      <Box style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={`${confirmedCount} Confirmed`}
          onClick={() => {
            setCategoryFilter('all')
            setReadinessFilter('all')
            setStatusFilter(statusFilter === 'confirmed' ? 'all' : 'confirmed')
          }}
          style={{
            backgroundColor: statusFilter === 'confirmed' ? '#2e7d32' : '#e8f5e9',
            color: statusFilter === 'confirmed' ? '#fff' : '#2e7d32',
            cursor: 'pointer'
          }}
        />
        <Chip
          size="small"
          label={`${reviewCount} Needs Review`}
          onClick={() => {
            setCategoryFilter('all')
            setReadinessFilter('all')
            setStatusFilter(statusFilter === 'needs_review' ? 'all' : 'needs_review')
          }}
          style={{
            backgroundColor: statusFilter === 'needs_review' ? '#1565c0' : '#e3f2fd',
            color: statusFilter === 'needs_review' ? '#fff' : '#1565c0',
            cursor: 'pointer'
          }}
        />
        {conflictCount > 0 && (
          <Chip
            size="small"
            label={`${conflictCount} Conflicts`}
            onClick={() => {
              setCategoryFilter('all')
              setReadinessFilter('all')
              setStatusFilter(statusFilter === 'quantity_conflict' ? 'all' : 'quantity_conflict')
            }}
            style={{
              backgroundColor: statusFilter === 'quantity_conflict' ? '#e65100' : '#fff3e0',
              color: statusFilter === 'quantity_conflict' ? '#fff' : '#e65100',
              cursor: 'pointer'
            }}
          />
        )}
      </Box>

      <Box style={{
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: 6,
        padding: '10px 12px',
        marginBottom: 12
      }}>
        <Typography variant="body2" style={{ color: '#555' }}>
          Assembly Items group evidence from bid schedule rows, plan tables and takeoffs, vendor quotes, and service recipes so each priced item can be reviewed before output.
        </Typography>
      </Box>
      <MatchSuggestionsPanel
        projectId={projectId}
        url={url}
        onMatchConfirmed={() => {
          fetchData()
          setSnackMsg('Source match confirmed. Rebuild Items to merge matched bid, plan, and vendor evidence.')
        }}
      />

      {/* Category filter */}
      <Box style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={`All (${items.length})`}
          onClick={() => {
            setCategoryFilter('all')
            setStatusFilter('all')
          }}
          style={{
            backgroundColor: categoryFilter === 'all' && statusFilter === 'all' ? 'var(--df-primary, #1f5e3a)' : '#e0e0e0',
            color: categoryFilter === 'all' && statusFilter === 'all' ? '#fff' : '#333',
            cursor: 'pointer'
          }}
        />
        <Chip
          size="small"
          label={`Uncategorized (${items.filter(i => !i.category).length})`}
          onClick={() => setCategoryFilter('uncategorized')}
          style={{
            backgroundColor: categoryFilter === 'uncategorized' ? 'var(--df-primary, #1f5e3a)' : '#e0e0e0',
            color: categoryFilter === 'uncategorized' ? '#fff' : '#333',
            cursor: 'pointer'
          }}
        />
        {CATEGORIES.filter(cat => items.some(i => i.category === cat)).map(cat => (
          <Chip
            key={cat}
            size="small"
            label={`${cat} (${items.filter(i => i.category === cat).length})`}
            onClick={() => setCategoryFilter(cat)}
            style={{
              backgroundColor: categoryFilter === cat ? (CATEGORY_COLORS[cat] || 'var(--df-primary, #1f5e3a)') : '#e0e0e0',
              color: categoryFilter === cat ? '#fff' : '#333',
              cursor: 'pointer'
            }}
          />
        ))}
      </Box>

      {items.length === 0 ? (
        <Box style={{ padding: 40, textAlign: 'center', color: '#999' }}>
          <Typography variant="body1">No Assembly Items yet.</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Generate Assembly Items to group evidence from the bid schedule, plans, vendor quotes, and recipes into reviewable estimating work.
          </Typography>
        </Box>
      ) : (
        <>
        {selected.size > 0 && (
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            backgroundColor: '#e3f2fd', borderRadius: 4, marginBottom: 8
          }}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {selected.size} selected
            </Typography>
            <Autocomplete
              options={CATEGORIES}
              value={bulkCategory || null}
              onChange={(e, val) => setBulkCategory(val || '')}
              size="small"
              style={{ width: 180 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Set Category"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    style: { ...params.InputProps.style, fontSize: 12, backgroundColor: '#fff' }
                  }}
                />
              )}
            />
            <ServiceAutocomplete
              services={services}
              value={bulkServiceId}
              category={bulkCategory || activeServiceCategory}
              onChange={setBulkServiceId}
              placeholder="Pick service"
              width={300}
            />
            {(() => {
              const selItems = items.filter(i => selected.has(i._id))
              const allConfirmed = selItems.length > 0 && selItems.every(i => i.status === 'confirmed')
              const allNotInScope = selItems.length > 0 && selItems.every(i => i.status === 'not_in_scope')
              return (
                <>
                  <Button
                    variant={allConfirmed ? 'contained' : 'outlined'}
                    size="small"
                    onClick={handleBulkConfirmSelected}
                    style={allConfirmed
                      ? { textTransform: 'none', backgroundColor: '#2e7d32', color: '#fff', borderColor: '#2e7d32' }
                      : { textTransform: 'none' }
                    }
                  >
                    {allConfirmed ? '\u2713 Confirmed' : 'Confirm'}
                  </Button>
                  <Button
                    variant={allNotInScope ? 'contained' : 'outlined'}
                    size="small"
                    onClick={handleBulkNotInScope}
                    style={allNotInScope
                      ? { textTransform: 'none', backgroundColor: '#b71c1c', color: '#fff', borderColor: '#b71c1c' }
                      : { textTransform: 'none', color: '#b71c1c', borderColor: '#b71c1c' }
                    }
                  >
                    {allNotInScope ? '\u2717 Not In Scope' : 'Not In Scope'}
                  </Button>
                </>
              )
            })()}
            <span style={{ display: 'inline-block', width: 1, height: 24, background: 'var(--line-2, #ccc)', margin: '0 8px' }} />
            <Button
              variant="contained"
              size="small"
              onClick={handleBulkSave}
              disabled={!bulkCategory && !bulkServiceId && ![...selected].some(id => dirtyIds.has(id))}
              style={{ textTransform: 'none', backgroundColor: 'var(--df-primary, #1f5e3a)', color: '#fff', paddingLeft: 20, paddingRight: 20 }}
            >
              Save{[...selected].some(id => dirtyIds.has(id)) ? ' *' : ''}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => { setSelected(new Set()); setBulkCategory(''); setBulkServiceId(''); setDirtyIds(new Set()) }}
              style={{ textTransform: 'none', color: '#999' }}
            >
              Clear
            </Button>
          </Box>
        )}
        <Table size="small" style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
          <TableHead>
            <TableRow style={{ background: 'var(--df-primary, #1f5e3a)' }}>
              <TableCell padding="checkbox" style={{ color: '#fff', fontWeight: 700, fontSize: 11, width: 40 }}>
                <Checkbox
                  size="small"
                  checked={filteredItems.length > 0 && filteredItems.every(i => selected.has(i._id))}
                  indeterminate={filteredItems.some(i => selected.has(i._id)) && !filteredItems.every(i => selected.has(i._id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelected(prev => {
                        const next = new Set(prev)
                        filteredItems.forEach(i => next.add(i._id))
                        return next
                      })
                    } else {
                      setSelected(prev => {
                        const next = new Set(prev)
                        filteredItems.forEach(i => next.delete(i._id))
                        return next
                      })
                    }
                  }}
                  style={{ color: '#fff' }}
                />
              </TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Assembly Item</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Kind</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Category</TableCell>
              <TableCell align="right" style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Qty</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Unit</TableCell>
              <TableCell align="right" style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Price</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Evidence</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Service Link</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11, width: 140 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map(item => {
              const expanded = expandedId === item._id
              const counts = evidenceCounts(item)
              const linkedService = serviceById.get(item.serviceId)
              const missingInputs = missingInputSlots(item, linkedService)
              const reviewInputs = inputReviewSlots(item, linkedService)
              const inferredInputs = inferredInputKeys(item, linkedService)
              return (
                <React.Fragment key={item._id}>
                <TableRow
                  hover
                  style={{
                    cursor: 'pointer',
                    ...(item.status === 'not_in_scope' ? { opacity: 0.4, textDecoration: 'line-through' } : {})
                  }}
                  onClick={(e) => {
                    // Don't toggle when clicking interactive elements inside the row
                    if (!e.target.closest || e.target.closest('button, a, input, select, [role="button"], [role="combobox"], .MuiAutocomplete-root, .MuiSelect-root')) return
                    setSelected(prev => {
                      const next = new Set(prev)
                      if (next.has(item._id)) next.delete(item._id)
                      else next.add(item._id)
                      return next
                    })
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.has(item._id)}
                      onChange={(e) => {
                        e.stopPropagation()
                        setSelected(prev => {
                          const next = new Set(prev)
                          if (e.target.checked) next.add(item._id)
                          else next.delete(item._id)
                          return next
                        })
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: 12, fontWeight: 500, maxWidth: 300 }}>
                    {item.description}
                    {missingInputs.length > 0 && (
                      <Chip
                        size="small"
                        label={`${missingInputs.length} input${missingInputs.length === 1 ? '' : 's'} needed`}
                        style={{ marginLeft: 6, height: 18, fontSize: 9, backgroundColor: '#ffebee', color: '#b71c1c' }}
                      />
                    )}
                    {missingInputs.length === 0 && inferredInputs.size > 0 && (
                      <Chip
                        size="small"
                        label={`${inferredInputs.size} input inferred`}
                        style={{ marginLeft: 6, height: 18, fontSize: 9, backgroundColor: '#fff8e1', color: '#8a5a00' }}
                      />
                    )}
                    {reviewInputs.length > 0 && missingInputs.length === 0 && inferredInputs.size === 0 && (
                      <Chip
                        size="small"
                        label={`${reviewInputs.length} input${reviewInputs.length === 1 ? '' : 's'}`}
                        style={{ marginLeft: 6, height: 18, fontSize: 9, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                      />
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedId(expanded ? null : item._id)
                      }}
                      style={{ marginLeft: 6, minWidth: 0, padding: '1px 4px', fontSize: 10, textTransform: 'none' }}
                    >
                      {expanded ? 'Hide' : reviewInputs.length > 0 ? 'Enter Inputs' : 'Review'}
                    </Button>
                    {(() => {
                      const bidSrc = itemSources(item).find(s => s.sourceType === 'bid_response' && s.sourcePackageId)
                      if (!bidSrc) return null
                      return (
                        <IconButton
                          size="small"
                          title="View in bid response"
                          style={{ marginLeft: 4, padding: 2 }}
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(`/bid-estimation/${projectId}/bid-response/${bidSrc.sourcePackageId}`, '_blank')
                          }}
                        >
                          <OpenInNewIcon style={{ fontSize: 12, color: '#1565c0' }} />
                        </IconButton>
                      )
                    })()}
                    <VendorCatalogLinkChips
                      item={item}
                      url={url}
                      onOpenProduct={setVendorProductId}
                      onMessage={setSnackMsg}
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: 11, color: '#666' }}>
                    {KIND_LABELS[item.kind] || item.kind}
                  </TableCell>
                  <TableCell style={{ fontSize: 11 }}>
                    {item.category ? (
                      <Chip
                        size="small"
                        label={item.category}
                        style={{
                          backgroundColor: CATEGORY_COLORS[item.category] || '#e0e0e0',
                          color: '#fff',
                          fontSize: 9,
                          height: 18
                        }}
                      />
                    ) : (
                      <span style={{ color: '#ccc' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="right" style={{ fontSize: 12, fontWeight: 600 }}>
                    {item.userQtyOverride != null ? (
                      <span style={{ color: '#1565c0' }}>{fmt(item.userQtyOverride)}</span>
                    ) : (
                      fmt(item.quantity)
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, color: '#888' }}>{item.unit}</TableCell>
                  <TableCell align="right" style={{ fontSize: 11 }}>
                    {item.vendorPricing?.unitPrice > 0 ? (
                      <Chip
                        size="small"
                        label={`$${item.vendorPricing.unitPrice.toFixed(2)}`}
                        style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: 10, height: 18 }}
                      />
                    ) : (
                      <span style={{ color: '#ccc' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: 11 }}>
                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <EvidencePill type="bid" count={counts.bid} />
                      <EvidencePill type="takeoff" count={counts.takeoff} />
                      <EvidencePill type="vendor" count={counts.vendor} />
                      <EvidencePill type="service" count={counts.service} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <ServiceAutocomplete
                      services={services}
                      value={item.serviceId || ''}
                      category={item.category}
                      onChange={(serviceId) => handleServiceLink(item._id, serviceId)}
                      placeholder="Link service"
                      width={240}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={item.status}
                      onChange={(e) => handleStatusChange(item._id, e.target.value)}
                      variant="standard"
                      style={{ fontSize: 11, boxShadow: 'none' }}
                      disableUnderline
                      MenuProps={{ PaperProps: { style: { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' } } }}
                      renderValue={(val) => (
                        <Chip
                          size="small"
                          label={STATUS_CHIPS[val]?.label || val}
                          style={{
                            backgroundColor: STATUS_CHIPS[val]?.bg || '#eee',
                            color: STATUS_CHIPS[val]?.color || '#333',
                            fontSize: 10,
                            height: 20
                          }}
                        />
                      )}
                    >
                      {Object.entries(STATUS_CHIPS).map(([key, cfg]) => (
                        <MenuItem key={key} value={key} style={{ fontSize: 12 }}>{cfg.label}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow>
                    <TableCell colSpan={10} style={{ backgroundColor: '#fafafa', padding: 16 }}>
                      <Box style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16 }}>
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Evidence</Typography>
                          {itemSources(item).length === 0 && counts.bid + counts.takeoff + counts.vendor === 0 ? (
                            <Typography variant="body2" style={{ color: '#999', marginTop: 6 }}>No source evidence attached.</Typography>
                          ) : (
                            <Box style={{ marginTop: 6 }}>
                              {itemSources(item).slice(0, 8).map((s, i) => (
                                <Box key={i} style={{ fontSize: 12, color: '#555', marginBottom: 5 }}>
                                  <span style={{
                                    display: 'inline-block',
                                    minWidth: 38,
                                    padding: '1px 5px',
                                    borderRadius: 3,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    backgroundColor: s.sourceType === 'bid_response' ? '#e3f2fd' : s.sourceType === 'vendor_quote' ? '#fff3e0' : '#e8f5e9',
                                    color: s.sourceType === 'bid_response' ? '#1565c0' : s.sourceType === 'vendor_quote' ? '#e65100' : '#2e7d32',
                                    marginRight: 6
                                  }}>
                                    {s.sourceType === 'bid_response' ? 'BID' : s.sourceType === 'vendor_quote' ? 'VQ' : 'PLAN'}
                                  </span>
                                  {s.sourceLabel || 'Source'}
                                  {s.quantity > 0 && <span style={{ color: '#777', marginLeft: 6 }}>{fmt(s.quantity)} {s.unit}</span>}
                                </Box>
                              ))}
                            </Box>
                          )}
                          <OutputAllocationEditor
                            item={item}
                            onSave={(outputLines) => handleItemPatch(item._id, { outputLines }, 'Output allocation saved')}
                          />
                          <EvidenceSourcePicker
                            item={item}
                            onSave={(patch, message) => handleItemPatch(item._id, patch, message)}
                          />
                          <BidEvidencePanel item={item} projectId={projectId} url={url} />
                          <Box style={{ marginTop: 12 }}>
                            <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Vendor Catalog</Typography>
                            <VendorCatalogLinkChips
                              item={item}
                              url={url}
                              onOpenProduct={setVendorProductId}
                              onMessage={setSnackMsg}
                            />
                            {vendorCatalogRefs(item).length === 0 && (
                              <Typography variant="body2" style={{ color: '#999', marginTop: 6, fontSize: 12 }}>
                                No vendor SKU or catalog match attached yet.
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>Resolved Quantity</Typography>
                          <Box style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <TextField
                              variant="outlined"
                              size="small"
                              defaultValue={item.userQtyOverride ?? item.resolvedQty ?? item.quantity ?? ''}
                              onBlur={(e) => {
                                const next = Number(e.target.value)
                                if (Number.isFinite(next)) {
                                  handleItemPatch(item._id, {
                                    userQtyOverride: next,
                                    resolvedQty: next,
                                    qtySource: 'manual'
                                  }, 'Quantity override saved')
                                }
                              }}
                              InputProps={{ style: { fontSize: 12, backgroundColor: '#fff' } }}
                            />
                            <TextField
                              variant="outlined"
                              size="small"
                              defaultValue={item.resolvedUnit || item.unit || ''}
                              onBlur={(e) => {
                                const next = String(e.target.value || '').trim()
                                if (next) {
                                  handleItemPatch(item._id, {
                                    resolvedUnit: next,
                                    unit: next,
                                    qtySource: 'manual'
                                  }, 'Unit override saved')
                                }
                              }}
                              InputProps={{ style: { fontSize: 12, backgroundColor: '#fff', width: 80 } }}
                            />
                          </Box>
                          <Typography variant="caption" style={{ display: 'block', color: '#777', marginTop: 6 }}>
                            Source: {item.qtySource || item.selectedQtySource || 'not selected'}
                          </Typography>
                        </Box>

                        <VariableEditor
                          item={item}
                          service={linkedService}
                          onSave={(variables) => handleItemPatch(item._id, { variables }, 'Variables saved')}
                        />
                        <ClassBreakdownEditor
                          item={item}
                          service={linkedService}
                          onSave={(classBreakdowns) => handleItemPatch(item._id, { classBreakdowns }, 'Class breakdowns saved')}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
        </>
      )}

      <Snackbar
        open={!!snackMsg}
        autoHideDuration={3000}
        onClose={() => setSnackMsg('')}
        message={snackMsg}
      />
      <VendorProductDetail
        productId={vendorProductId}
        open={!!vendorProductId}
        onClose={() => setVendorProductId(null)}
      />
    </div></div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(ScopeReconciler)
